import { useLocation, useNavigate, useParams } from "react-router-dom";
import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";

import MainContainer from "../../../layout/MainContainer";
import { DocumentViewer } from "../../../components";
import { Earning, TutorRating } from "../../../features";
import {
  useLazyGetTeachingDetailsByIdQuery,
  useLazyGetTutorDetailsQuery,
  useLazyGetnextTutorByIdQuery,
} from "../../../services/tutor";
import { EmbedPDF } from "@simplepdf/react-embed-pdf";

import Loader from "../../../helpers/Loader";
import { TutorDetails, Document } from "../../../types/General";
import { useLazyGetDocucmentsDetailsQuery } from "../../../services/documentation";
import moment from "moment";
import { errorToast } from "../../../helpers";

import ActivityHistoryTutor from "../../../features/tutor/ActivityHistory";
import Classes from "../../../features/viewTutorDetails/Classes";
import TeaserVideos from "../../../features/viewTutorDetails/TeaserVideos";
import ShortVideos from "../../../features/viewTutorDetails/ShortVideos";
import Forum from "../../../features/viewTutorDetails/Forum";
import Posts from "../../../features/viewTutorDetails/Posts";
import {
  CURRICULUM,
  DocumentType,
  GRADE_TYPE_NAME,
  TEACHING_LANGUAGE,
  TEACHING_STYLE,
} from "../../../helpers/constants/enums";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface TutorDetailData {
  _id?: string;
  name?: string;
  userName?: string;
  email?: string;
  dialCode?: string;
  phoneNo?: string;
  age?: string;
  gender?: string;
  timezone?: string;
  followers?: number;
  views?: number;
  shortBio?: string;
  address?: string;
  isBlocked?: boolean;
  image?: string;
  teachingdetails?: {
    totalTeachingExperience?: string;
    achievement?: string;
    specialization?: string;
    teachingLanguage?: TEACHING_LANGUAGE;
    classes?: number[];
    teachingStyle?: number[];
    price?: number;
    curriculum?: number[];
    curriculumOther?: number[];
  };
  bankdetails?: {
    accountHolderName?: string;
    accountNumber?: string;
    bankName?: string;
    swiftCode?: string;
  };
  subjectIds?: Array<{ name: string }>;
  classEarn?: number;
  giftsEarn?: number;
  oneOnOneEarn?: number;
  totalEarn?: number;
  balance?: number;
  withdrawAmount?: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const TutorDetail = () => {
  const location = useLocation();
  const { state } = location;
  const navigate = useNavigate();
  const [img, setImg] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);
  const [tutorDetails, setTutorDetails] = useState<TutorDetailData | null>(
    null
  );
  const [documents, setDocuments] = useState<Document[]>([]);
  const [value, setValue] = useState(0);

  const { _id } = useParams<{ _id: string }>();

  const [getTutorById, { isLoading: isTutorLoading }] =
    useLazyGetTutorDetailsQuery();
  const [getNextTutor] = useLazyGetnextTutorByIdQuery();
  const [getTeachingDetails] = useLazyGetTeachingDetailsByIdQuery();
  const [getDocs, { isLoading: isDocsLoading }] =
    useLazyGetDocucmentsDetailsQuery();

  const isLoading = isTutorLoading || isDocsLoading;

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const getDocumentName = useCallback((documentType: number): string => {
    switch (documentType) {
      case 1:
        return "Education Documents";
      case 2:
        return "Police Clearance Letter";
      case 3:
        return "National Identification Scan";
      case 4:
        return "Headshot image";
      case 5:
        return "Selfie Scan";
      default:
        return "Id Proof";
    }
  }, []);

  const fetchTutorData = useCallback(
    async (id: string) => {
      try {
        const response = await getTutorById({ id }).unwrap();
        if (response.statusCode === 200) {
          setTutorDetails(response.data[0] || null);
        }
      } catch (error) {
        console.error("Failed to fetch tutor data:", error);
        errorToast("Failed to load tutor details");
      }
    },
    [getTutorById]
  );

  const fetchNextTutor = useCallback(
    async (id: string) => {
      try {
        const response = await getNextTutor({ userId: id }).unwrap();
        if (response.statusCode === 200 && response.data?._id) {
          navigate(
            `/manage-tutor/details/NextTutorDetail/${response.data._id}`
          );
        } else {
          errorToast("No next tutor available");
        }
      } catch (error) {
        console.error("Failed to fetch next tutor:", error);
        errorToast("No next tutor available");
      }
    },
    [getNextTutor, navigate]
  );

  const fetchTeachingDetails = useCallback(
    async (id: string) => {
      try {
        await getTeachingDetails({ userId: id }).unwrap();
      } catch (error) {
        console.error("Failed to fetch teaching details:", error);
      }
    },
    [getTeachingDetails]
  );

  const fetchDocuments = useCallback(
    async (id: string) => {
      try {
        const response = await getDocs({ userId: id }).unwrap();
        if (response?.statusCode === 200) {
          setDocuments(response.data || []);
        }
      } catch (error: any) {
        console.error("Failed to fetch documents:", error);
        errorToast(error?.data?.message || "Failed to load documents");
      }
    },
    [getDocs]
  );

  useEffect(() => {
    if (_id) {
      fetchTutorData(_id);
      fetchTeachingDetails(_id);
      fetchDocuments(_id);
    }
  }, [_id, fetchTutorData, fetchTeachingDetails, fetchDocuments]);

  const handleNextTutor = () => {
    if (_id) {
      fetchNextTutor(_id);
    }
  };

  const handleBack = () => {
    navigate("/manage-tutors");
  };

  const handleImageClick = (imageUrl: string) => {
    setImg(imageUrl);
    setOpen(true);
  };

  const renderDocumentSection = (documentType: DocumentType, title: string) => {
    const filteredDocs = documents.filter(
      (doc) => doc.documentType === documentType && doc.frontImage
    );

    if (filteredDocs.length === 0) return null;

    return (
      <Grid item xs={12}>
        <Box>
          <Typography component="h5" style={{ padding: 15, fontSize: "15px" }}>
            {title}
          </Typography>
          <Box className="docs_div">
            <Grid container spacing={2}>
              {filteredDocs.map((item, index) => (
                <Grid item xs={4} key={index}>
                  {item.frontImage?.endsWith(".pdf") ? (
                    <div className="upload_pdf_preview">
                      <EmbedPDF companyIdentifier="yourcompany">
                        <a
                          href={item.frontImage}
                          className="pdf_uplod"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            src="/static/images/pdf.png"
                            alt="PDF document"
                          />
                          <h6>{getDocumentName(item.documentType)}</h6>
                        </a>
                      </EmbedPDF>
                    </div>
                  ) : (
                    <figure
                      onClick={() => handleImageClick(item.frontImage || "")}
                    >
                      <img
                        src={item.frontImage}
                        alt={getDocumentName(item.documentType)}
                      />
                    </figure>
                  )}
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </Grid>
    );
  };

  if (!tutorDetails && !isLoading) {
    return (
      <MainContainer>
        <div className="main_loyout">
          <Typography>No tutor data found</Typography>
        </div>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <div className="main_loyout">
        <div className="dashboard">
          <h1 className="mn_hdng">View Tutor Profile</h1>

          {_id && state?.userStatus !== "DELETED" && (
            <Button className="btn btn_primary" onClick={handleNextTutor}>
              Next
            </Button>
          )}
          <Button className="btn btn_primary" onClick={handleBack}>
            Back
          </Button>
        </div>

        <Loader isLoad={isLoading} />

        <Card className="cards">
          <CardContent sx={{ p: 1 }}>
            <Grid container spacing={2} className="view_box">
              <Grid item lg={2} md={2} sm={6} xs={12}>
                <figure
                  className="profile_img"
                  onClick={() =>
                    handleImageClick(
                      tutorDetails?.image ||
                        "/static/images/user_placeholder.png"
                    )
                  }
                >
                  <img
                    src={
                      tutorDetails?.image ||
                      `/static/images/user_placeholder.png`
                    }
                    alt="Tutor profile"
                  />
                </figure>
              </Grid>

              <Grid item xs={10} className="view_box_list">
                <Grid container spacing={3}>
                  {/* Personal Information */}
                  <Grid item lg={4} md={4} sm={6} xs={12}>
                    <Box>
                      <Typography component="h5">Name</Typography>
                      <Typography component="p">
                        {tutorDetails?.name || "-"}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item lg={4} md={4} sm={6} xs={12}>
                    <Box>
                      <Typography component="h5">Username</Typography>
                      <Typography component="p">
                        {tutorDetails?.userName || "-"}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item lg={4} md={4} sm={6} xs={12}>
                    <Box>
                      <Typography component="h5">Email</Typography>
                      <Typography component="p">
                        {tutorDetails?.email || "-"}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item lg={4} md={4} sm={6} xs={12}>
                    <Box>
                      <Typography component="h5">Phone no</Typography>
                      <Typography component="p">
                        {tutorDetails?.dialCode || ""}{" "}
                        {tutorDetails?.phoneNo || "-"}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item lg={4} md={4} sm={6} xs={12}>
                    <Box>
                      <Typography component="h5">Date Of Birth</Typography>
                      <Typography component="p">
                        {tutorDetails?.age
                          ? moment(tutorDetails.age).format("DD-MM-YYYY")
                          : "-"}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item lg={4} md={4} sm={6} xs={12}>
                    <Box>
                      <Typography component="h5">Gender</Typography>
                      <Typography component="p">
                        {tutorDetails?.gender === "MALE"
                          ? "Male"
                          : tutorDetails?.gender === "FEMALE"
                            ? "Female"
                            : "-"}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item lg={4} md={4} sm={6} xs={12}>
                    <Box>
                      <Typography component="h5">Timezone</Typography>
                      <Typography component="p">
                        {tutorDetails?.timezone || "-"}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item lg={4} md={4} sm={6} xs={12}>
                    <Box>
                      <Typography component="h5">Followers</Typography>
                      <Typography component="p">
                        {tutorDetails?.followers || "-"}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item lg={4} md={4} sm={6} xs={12}>
                    <Box>
                      <Typography component="h5">Views</Typography>
                      <Typography component="p">
                        {tutorDetails?.views || "-"}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item lg={4} md={4} sm={6} xs={12}>
                    <Box>
                      <Typography component="h5">Short Bio</Typography>
                      <Typography
                        component="div"
                        dangerouslySetInnerHTML={{
                          __html: tutorDetails?.shortBio || "",
                        }}
                      />
                    </Box>
                  </Grid>

                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Box>
                      <Typography component="h5">Address</Typography>
                      <Typography component="p">
                        {tutorDetails?.address || "-"}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item lg={4} md={4} sm={6} xs={12}>
                    <Box>
                      <Typography component="h5">Account Status</Typography>
                      <Typography component="p">
                        {tutorDetails?.isBlocked === false
                          ? "Active"
                          : "Blocked"}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Teaching Details */}
                  {tutorDetails?.teachingdetails && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="h6">Teaching Details</Typography>
                      </Grid>

                      <Grid item lg={4} md={4} sm={6} xs={12}>
                        <Box>
                          <Typography component="h5">
                            Total Teaching Experience
                          </Typography>
                          <Typography component="p">
                            {tutorDetails.teachingdetails
                              .totalTeachingExperience || "-"}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item lg={4} md={4} sm={6} xs={12}>
                        <Box>
                          <Typography component="h5">Achievement</Typography>
                          <Typography component="p">
                            {tutorDetails.teachingdetails.achievement || "-"}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item lg={4} md={4} sm={6} xs={12}>
                        <Box>
                          <Typography component="h5">Specialization</Typography>
                          <Typography component="p">
                            {tutorDetails.teachingdetails.specialization || "-"}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item lg={4} md={4} sm={6} xs={12}>
                        <Box>
                          <Typography component="h5">
                            Teaching Language
                          </Typography>
                          <Typography component="p">
                            {tutorDetails.teachingdetails.teachingLanguage ===
                            TEACHING_LANGUAGE.ENGLISH
                              ? "English"
                              : tutorDetails.teachingdetails
                                    .teachingLanguage ===
                                  TEACHING_LANGUAGE.ARABIC
                                ? "Arabic"
                                : "English, Arabic"}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item lg={4} md={4} sm={6} xs={12}>
                        <Box>
                          <Typography component="h5">Subjects</Typography>
                          <Typography component="p">
                            {tutorDetails.subjectIds?.map((item, index) =>
                              index === 0 ? item.name : ", " + item.name
                            ) || "-"}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item lg={4} md={4} sm={6} xs={12}>
                        <Box>
                          <Typography component="h5">Classes</Typography>
                          <Typography component="p">
                            {tutorDetails.teachingdetails.classes?.length
                              ? tutorDetails.teachingdetails.classes.map(
                                  (item, index) =>
                                    index === 0
                                      ? GRADE_TYPE_NAME[item]
                                      : ", " + GRADE_TYPE_NAME[item]
                                )
                              : "-"}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item lg={4} md={4} sm={6} xs={12}>
                        <Box>
                          <Typography component="h5">Teaching Style</Typography>
                          <Typography component="p">
                            {tutorDetails.teachingdetails.teachingStyle?.length
                              ? tutorDetails.teachingdetails.teachingStyle
                                  .map((item, index) =>
                                    index === 0
                                      ? TEACHING_STYLE[
                                          item as keyof typeof TEACHING_STYLE
                                        ]
                                      : ", " +
                                        TEACHING_STYLE[
                                          item as keyof typeof TEACHING_STYLE
                                        ]
                                  )
                                  .join("")
                              : "-"}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item lg={4} md={4} sm={6} xs={12}>
                        <Box>
                          <Typography component="h5">Price/hour</Typography>
                          <Typography component="p">
                            {tutorDetails.teachingdetails.price || "-"}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item lg={4} md={4} sm={6} xs={12}>
                        <Box>
                          <Typography component="h5">Curriculum</Typography>
                          <Typography component="p">
                            {tutorDetails.teachingdetails.curriculum?.length
                              ? tutorDetails.teachingdetails.curriculum
                                  .map((item, index) =>
                                    index === 0
                                      ? CURRICULUM[
                                          item as keyof typeof CURRICULUM
                                        ]
                                      : ", " +
                                        CURRICULUM[
                                          item as keyof typeof CURRICULUM
                                        ]
                                  )
                                  .join("")
                              : "-"}
                          </Typography>
                        </Box>
                      </Grid>
                    </>
                  )}

                  {/* Bank Details */}
                  {tutorDetails?.bankdetails && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="h6">Bank Details</Typography>
                      </Grid>

                      <Grid item lg={4} md={4} sm={6} xs={12}>
                        <Box>
                          <Typography component="h5">
                            Account Holder Name
                          </Typography>
                          <Typography component="p">
                            {tutorDetails.bankdetails.accountHolderName || "-"}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item lg={4} md={4} sm={6} xs={12}>
                        <Box>
                          <Typography component="h5">Account Number</Typography>
                          <Typography component="p">
                            {tutorDetails.bankdetails.accountNumber || "-"}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item lg={4} md={4} sm={6} xs={12}>
                        <Box>
                          <Typography component="h5">Bank Name</Typography>
                          <Typography component="p">
                            {tutorDetails.bankdetails.bankName || "-"}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item lg={4} md={4} sm={6} xs={12}>
                        <Box>
                          <Typography component="h5">Swift Code</Typography>
                          <Typography component="p">
                            {tutorDetails.bankdetails.swiftCode || "-"}
                          </Typography>
                        </Box>
                      </Grid>
                    </>
                  )}

                  {/* Earning Information */}
                  <Grid item xs={12}>
                    <Typography variant="h6">Earning</Typography>
                  </Grid>

                  <Grid item lg={4} md={4} sm={6} xs={12}>
                    <Box>
                      <Typography component="h5">Class Earn</Typography>
                      <Typography component="p">
                        {tutorDetails?.classEarn || "0"}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item lg={4} md={4} sm={6} xs={12}>
                    <Box>
                      <Typography component="h5">Gifts Earn</Typography>
                      <Typography component="p">
                        {tutorDetails?.giftsEarn || "0"}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item lg={4} md={4} sm={6} xs={12}>
                    <Box>
                      <Typography component="h5">One-On-One Earn</Typography>
                      <Typography component="p">
                        {tutorDetails?.oneOnOneEarn || "0"}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item lg={4} md={4} sm={6} xs={12}>
                    <Box>
                      <Typography component="h5">Total Earn</Typography>
                      <Typography component="p">
                        {tutorDetails?.totalEarn || "0"}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item lg={4} md={4} sm={6} xs={12}>
                    <Box>
                      <Typography component="h5">Balance</Typography>
                      <Typography component="p">
                        {tutorDetails?.balance || "0"}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item lg={4} md={4} sm={6} xs={12}>
                    <Box>
                      <Typography component="h5">Withdraw Amount</Typography>
                      <Typography component="p">
                        {tutorDetails?.withdrawAmount || "0"}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Document Sections */}
                  {renderDocumentSection(DocumentType.HEADSHOT, "Id Proof")}
                  {renderDocumentSection(
                    DocumentType.POLICE_CLEARANCE,
                    "Experience"
                  )}
                  {renderDocumentSection(DocumentType.NATIONAL_ID, "Education")}
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Tabs Section */}
        <Card sx={{ mt: 4 }} className="cards">
          <Box className="custom_tabs">
            <Box className="flx_sc">
              <Tabs
                value={value}
                onChange={handleChange}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="tutor detail tabs"
                className="custom_tabs_links"
              >
                <Tab label="Earnings" {...a11yProps(0)} />
                <Tab label="Activity History" {...a11yProps(1)} />
                <Tab label="Ratings" {...a11yProps(2)} />
                <Tab label="Teaser Video" {...a11yProps(3)} />
                <Tab label="Short Video" {...a11yProps(4)} />
                <Tab label="Classes" {...a11yProps(5)} />
                <Tab label="Forums" {...a11yProps(6)} />
                <Tab label="Posts" {...a11yProps(7)} />
              </Tabs>
            </Box>

            <CustomTabPanel value={value} index={0}>
              <Earning />
            </CustomTabPanel>

            <CustomTabPanel value={value} index={1}>
              <ActivityHistoryTutor />
            </CustomTabPanel>

            <CustomTabPanel value={value} index={2}>
              <TutorRating />
            </CustomTabPanel>

            <CustomTabPanel value={value} index={3}>
              <TeaserVideos />
            </CustomTabPanel>

            <CustomTabPanel value={value} index={4}>
              <ShortVideos />
            </CustomTabPanel>

            <CustomTabPanel value={value} index={5}>
              <Classes />
            </CustomTabPanel>

            <CustomTabPanel value={value} index={6}>
              <Forum />
            </CustomTabPanel>

            <CustomTabPanel value={value} index={7}>
              <Posts />
            </CustomTabPanel>
          </Box>
        </Card>
      </div>

      <DocumentViewer open={open} setOpen={setOpen} img={img} />
    </MainContainer>
  );
};

export default TutorDetail;
